import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, Alert, Chip, TextField, IconButton, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import { Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { useMonitoringReportShared } from '../../shared/context/MonitoringReportContext';
import ProjectPreviousFundingsTable from '../../../project/ProjectPreviousFundingsTable';
import { formatCurrency } from '../../../../common/utils/common.util';
import { ProjectPreviousFundingDTO } from '../../../project/project.type';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { DrawContext, FieldType, SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { SectionCompleteToggle } from '../../shared/components/SectionCompleteToggle';
import { useCompletionToggleEffect } from '../../../../hooks/useCompletionToggleEffect';
import { BookmarkToggle } from './BookmarkToggle';
import { getNextActionItemOrder, getSectionRank } from './action-items.util';
import { ActionItemsReorderModal } from './ActionItemsReorderModal';
import {
  buildPreviousFundingsAutoComment,
  isUneditedPreviousFundingsComment,
  summarizePreviousFundingTable,
} from './previous-fundings-comment.util';

const NOTE_FIELD_NAME = 'Note';

const PREVIOUS_FUNDINGS_SECTION_KEY = 'previous-fundings';

const PreviousFundingsSection: React.FC = () => {
  const { reportState, setReportState, setIsDirty, isDirty, saveReport, claimActionItemOrder } = useMonitoringReportShared() as any;
  const { drawSummary } = reportState;

  const comment = reportState.previousFundingsComment || '';
  const sections = (reportState.sections || {}) as any;
  const previousFundingsSection = (sections['previous-fundings'] ||
    sections.previousFundings ||
    sections.previousFunding ||
    sections.previousFundingsSection) as SectionFieldCollection | undefined;
  const isCompleted = Boolean(previousFundingsSection?.isCompleted);

  // One table per payapp, oldest application first. Derived in render (not copied into state by an
  // effect) so the comment is always built from the same table snapshot the tables show.
  const draws: DrawContext[] = useMemo(() => {
    const fromState = reportState.draws as DrawContext[] | undefined;
    if (fromState?.length) return fromState;
    if (!drawSummary) return [];
    return [{ drawId: drawSummary.id, drawSummary }];
  }, [reportState.draws, drawSummary]);
  const isMultiPayapp = draws.length > 1;
  const tableStates = useMemo(
    () =>
      draws.map(draw =>
        summarizePreviousFundingTable(draw.drawId, draw.drawSummary?.name || '[G702 Name]', draw.drawSummary?.previousFundings),
      ),
    [draws],
  );
  const autoComment = useMemo(() => buildPreviousFundingsAutoComment(tableStates), [tableStates]);

  const [expanded, setExpanded] = useState(() => !isCompleted);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const sectionRank = useMemo(() => getSectionRank(reportState.sections, PREVIOUS_FUNDINGS_SECTION_KEY), [reportState.sections]);
  const previousCaseKey = useRef<string | null>(null);
  // The auto-comment last written, so an unedited one can follow later table edits.
  const lastAutoComment = useRef<string | null>(null);
  const previousFundingsSectionCommentsField = (previousFundingsSection ? getCommentsField(previousFundingsSection) : undefined) || {
    fieldName: 'Comments',
    fieldType: FieldType.TextArray,
    entries: [],
  };
  const noteField = previousFundingsSection?.fields?.find((f: SectionFieldDTO) => f.fieldName === NOTE_FIELD_NAME);
  const noteBookmarked = Boolean(noteField?.addToActionItems);

  // Note and Comments share one order so the section moves as a unit, like other sections
  const onBook = (): number => {
    const bookedOrders = [
      ...(noteBookmarked && typeof noteField?.actionItemOrder === 'number' ? [noteField.actionItemOrder] : []),
      ...(previousFundingsSectionCommentsField.entries || [])
        .filter(e => e.addToActionItems && typeof e.actionItemOrder === 'number')
        .map(e => e.actionItemOrder as number),
    ];
    if (bookedOrders.length) return Math.min(...bookedOrders);
    return claimActionItemOrder ? claimActionItemOrder() : getNextActionItemOrder(sections);
  };

  useEffect(() => {
    const isInitialLoad = previousCaseKey.current === null;
    // A new case (match ↔ mismatch, another variant, or a different set of differing payapps) replaces
    // even an edited note; within the same case only an unedited auto-comment follows the amounts.
    const caseChanged = !isInitialLoad && previousCaseKey.current !== autoComment.caseKey;
    const existingComment = reportState.previousFundingsComment || '';
    const isUnedited = existingComment === lastAutoComment.current || isUneditedPreviousFundingsComment(existingComment, autoComment);

    if ((caseChanged || isUnedited) && existingComment !== autoComment.comment) {
      // On load this regenerates for the current case, same as the "Restore auto-comment" button.
      if (!isInitialLoad) {
        setIsDirty(true);
      }
      setReportState((prev: any) => ({
        ...prev,
        previousFundingsComment: autoComment.comment,
      }));
    }

    lastAutoComment.current = autoComment.comment;
    previousCaseKey.current = autoComment.caseKey;
  }, [autoComment]);

  /**
   * Writes a previous fundings edit to one payapp's draw summary.
   *
   * Writes `draws` only — `drawSummary` is derived from it by the context, so writing the mirror
   * directly would have no effect.
   */
  const handleDrawEditChange = (drawId: string, field: string, value: any) => {
    setIsDirty(true);
    setReportState((prev: any) => {
      const prevDraws: any[] = prev.draws?.length
        ? prev.draws
        : prev.drawSummary
          ? [{ drawId: prev.drawSummary.id, drawSummary: prev.drawSummary, budgetSummary: prev.budgetSummary, lineItems: prev.lineItems }]
          : [];

      if (!prevDraws.some(draw => draw.drawId === drawId)) {
        // No such draw (shouldn't happen once a report is open) — nothing to write.
        return prev;
      }

      const nextDraws = prevDraws.map(draw =>
        draw.drawId === drawId ? { ...draw, drawSummary: { ...draw.drawSummary, [field]: value } } : draw,
      );

      return { ...prev, draws: nextDraws };
    });
  };

  const handleCommentChange = (newComment: string) => {
    setIsDirty(true);
    setReportState((prev: any) => ({
      ...prev,
      previousFundingsComment: newComment,
    }));
  };

  const handleRefreshComment = () => {
    setIsDirty(true);
    setReportState((prev: any) => ({
      ...prev,
      previousFundingsComment: autoComment.comment,
    }));
    lastAutoComment.current = autoComment.comment;
  };

  const handlePreviousFundingsSectionCommentsChange = (updatedCommentsField: SectionFieldDTO) => {
    setIsDirty(true);
    setReportState((prev: any) => {
      const stateSections = (prev.sections || {}) as any;
      const sectionKey = PREVIOUS_FUNDINGS_SECTION_KEY;
      const currentSection = stateSections[sectionKey] ||
        stateSections.previousFundings ||
        stateSections.previousFunding ||
        stateSections.previousFundingsSection || { fields: [] };

      const otherFields = (currentSection.fields || []).filter(
        (field: SectionFieldDTO) => field.fieldName !== updatedCommentsField.fieldName,
      );
      const updatedSection: SectionFieldCollection = {
        ...currentSection,
        fields: [...otherFields, updatedCommentsField],
      };

      return {
        ...prev,
        sections: {
          ...stateSections,
          [sectionKey]: updatedSection,
        },
      };
    });
  };

  const commentEntries = previousFundingsSectionCommentsField.entries || [];
  const allBookmarked = noteBookmarked && commentEntries.every(e => e.addToActionItems);
  const bookmarkAllTooltip = allBookmarked ? 'Remove all comment bookmarks' : 'Bookmark all comments';

  const handleToggleAllBookmarks = () => {
    setIsDirty(true);
    const nextValue = !allBookmarked;
    // Claimed outside the updater so StrictMode's double invoke doesn't claim twice
    const order = nextValue ? onBook() : undefined;
    const nextOrder = (current?: number, wasBookmarked?: boolean) => (nextValue ? (wasBookmarked ? current : order) : undefined);
    setReportState((prev: any) => {
      const stateSections = (prev.sections || {}) as any;
      const currentSection = stateSections[PREVIOUS_FUNDINGS_SECTION_KEY] ||
        stateSections.previousFundings ||
        stateSections.previousFunding ||
        stateSections.previousFundingsSection || { fields: [] };
      const fields: SectionFieldDTO[] = currentSection.fields || [];
      const currentNote = fields.find(f => f.fieldName === NOTE_FIELD_NAME);
      const currentComments = fields.find(f => f.fieldName === 'Comments');

      const updatedNote: SectionFieldDTO = {
        fieldName: NOTE_FIELD_NAME,
        fieldType: FieldType.Text,
        ...currentNote,
        addToActionItems: nextValue,
        actionItemOrder: nextOrder(currentNote?.actionItemOrder, currentNote?.addToActionItems),
      };
      const updatedFields = [
        ...fields
          .filter(f => f.fieldName !== NOTE_FIELD_NAME)
          .map(f =>
            f === currentComments
              ? {
                  ...f,
                  entries: (f.entries || []).map(e => ({
                    ...e,
                    addToActionItems: nextValue,
                    actionItemOrder: nextOrder(e.actionItemOrder, e.addToActionItems),
                  })),
                }
              : f,
          ),
        updatedNote,
      ];

      const { previousFundings, previousFunding, previousFundingsSection, ...restSections } = stateSections;
      return {
        ...prev,
        sections: { ...restSections, [PREVIOUS_FUNDINGS_SECTION_KEY]: { ...currentSection, fields: updatedFields } },
      };
    });
  };

  const handleToggleNoteBookmark = () => {
    setIsDirty(true);
    // Claimed outside the updater so StrictMode's double invoke doesn't claim twice
    const bookedOrder = noteBookmarked ? undefined : onBook();
    setReportState((prev: any) => {
      const stateSections = (prev.sections || {}) as any;
      const sectionKey = PREVIOUS_FUNDINGS_SECTION_KEY;
      const currentSection = stateSections[sectionKey] ||
        stateSections.previousFundings ||
        stateSections.previousFunding ||
        stateSections.previousFundingsSection || { fields: [] };

      const currentNoteField = currentSection.fields?.find((f: SectionFieldDTO) => f.fieldName === NOTE_FIELD_NAME);
      const nextAddToActionItems = !currentNoteField?.addToActionItems;
      const nextActionItemOrder = nextAddToActionItems ? (bookedOrder ?? getNextActionItemOrder(stateSections)) : undefined;

      // Value is intentionally not stored here — the live comment (reportState.previousFundingsComment)
      // is always the source of truth and is passed separately into buildMagicActionItemsHtml, so this
      // field only tracks bookmark state and avoids getting out of sync when the comment is edited.
      const otherFields = (currentSection.fields || []).filter((f: SectionFieldDTO) => f.fieldName !== NOTE_FIELD_NAME);
      const updatedNoteField: SectionFieldDTO = {
        fieldName: NOTE_FIELD_NAME,
        fieldType: FieldType.Text,
        ...currentNoteField,
        addToActionItems: nextAddToActionItems,
        actionItemOrder: nextActionItemOrder,
      };

      const { previousFundings, previousFunding, previousFundingsSection, ...restSections } = stateSections;

      const updatedSection: SectionFieldCollection = {
        ...currentSection,
        fields: [...otherFields, updatedNoteField],
      };

      return {
        ...prev,
        sections: {
          ...restSections,
          [sectionKey]: updatedSection,
        },
      };
    });
  };

  const handleToggleCompleted = useCallback(() => {
    setIsDirty(true);
    setReportState((prev: any) => {
      const stateSections = (prev.sections || {}) as any;
      const sectionKey = PREVIOUS_FUNDINGS_SECTION_KEY;
      const currentSection = stateSections[sectionKey] ||
        stateSections.previousFundings ||
        stateSections.previousFunding ||
        stateSections.previousFundingsSection || { fields: [] };
      const updatedSection: SectionFieldCollection = { ...currentSection, isCompleted: !currentSection.isCompleted };

      const { previousFundings, previousFunding, previousFundingsSection, ...restSections } = stateSections;

      return {
        ...prev,
        sections: {
          ...restSections,
          [sectionKey]: updatedSection,
        },
      };
    });
  }, [setReportState, setIsDirty]);

  const { isSavingCompletion } = useCompletionToggleEffect({
    isCompleted,
    onToggle: nextCompleted => setExpanded(!nextCompleted),
    saveReport,
  });

  const completeToggle = <SectionCompleteToggle isCompleted={isCompleted} onToggle={handleToggleCompleted} isSaving={isSavingCompletion} />;

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{ px: 2, '&.MuiAccordion-root': { borderRadius: '10px' }, '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {completeToggle}
            <Typography variant='h5' color='primary' sx={{ lineHeight: 1.5, opacity: isCompleted ? 0.6 : 1 }}>
              Previous Fundings
            </Typography>
          </Box>
          {sectionRank !== undefined ? (
            <Box sx={{ display: 'flex', alignItems: 'center', borderRadius: '999px', bgcolor: 'grey.200', overflow: 'hidden' }}>
              <Tooltip title='Reorder action items' placement='top'>
                <Box
                  onClick={e => {
                    e.stopPropagation();
                    setReorderModalOpen(true);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    pl: 1.5,
                    pr: 1.25,
                    py: 0.625,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'grey.300' },
                    transition: 'background-color 0.15s',
                  }}
                >
                  <DragIndicatorIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
                    {sectionRank}
                  </Typography>
                </Box>
              </Tooltip>
              <Box sx={{ width: '1px', height: 20, bgcolor: 'grey.400', flexShrink: 0 }} />
              <Tooltip title={bookmarkAllTooltip} placement='top'>
                <Box
                  onClick={e => {
                    e.stopPropagation();
                    handleToggleAllBookmarks();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pl: 1.25,
                    pr: 1.5,
                    py: 0.625,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.300' },
                    transition: 'background-color 0.15s',
                  }}
                >
                  {allBookmarked ? (
                    <BookmarkIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                  ) : (
                    <BookmarkBorderIcon sx={{ fontSize: '1.2rem', color: 'text.disabled' }} />
                  )}
                </Box>
              </Tooltip>
            </Box>
          ) : (
            <Tooltip title={bookmarkAllTooltip} placement='top'>
              <Box sx={{ display: 'flex' }}>
                <BookmarkToggle
                  addToActionItems={allBookmarked}
                  onToggle={handleToggleAllBookmarks}
                  iconSize='medium'
                  sx={{ display: 'flex', alignItems: 'center' }}
                  ariaLabel={bookmarkAllTooltip}
                />
              </Box>
            </Tooltip>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, pb: 2, position: 'relative' }}>
        <Box aria-hidden={isCompleted} {...(isCompleted ? { inert: '' } : {})} sx={isCompleted ? { opacity: 0.6 } : undefined}>
          {draws.map((draw, index) => {
            const tableState = tableStates[index];
            return (
              <Box key={draw.drawId} sx={{ mt: index > 0 ? 3 : 0 }}>
                {/* With several payapps each table is labelled with its G702, and flagged when it differs. */}
                {isMultiPayapp && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant='subtitle1' fontWeight='bold' sx={{ m: 0 }}>
                      {tableState.label}
                    </Typography>
                    <Chip
                      size='small'
                      variant='outlined'
                      color={tableState.hasDifference ? 'warning' : 'success'}
                      label={tableState.hasDifference ? 'Difference' : 'No difference'}
                    />
                  </Box>
                )}
                <ProjectPreviousFundingsTable
                  previousFundings={draw.drawSummary?.previousFundings}
                  onChange={(updated: ProjectPreviousFundingDTO[]) => handleDrawEditChange(draw.drawId, 'previousFundings', updated)}
                  isMagicReport={true}
                  isDirty={isDirty}
                />

                {tableState.showMismatchAlert && (
                  <Alert severity='warning' sx={{ mt: 2, py: 0.5 }}>
                    <Typography variant='body2' fontWeight='bold' sx={{ m: 0, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                      Discrepancy between expected and payapp Previous Funding amount{isMultiPayapp ? ` for ${tableState.label}` : ''}.
                      Enter comment.
                    </Typography>
                    <Typography variant='body2' sx={{ m: 0, mt: 0.5, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                      <strong>Received by Contractor:</strong> {formatCurrency(tableState.receivedTotal.toString())}
                    </Typography>
                    <Typography variant='body2' sx={{ m: 0, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                      <strong>Funded by Lender (per last CDCC recommendation):</strong>{' '}
                      {formatCurrency(tableState.fundedByLenderTotal.toString())}
                    </Typography>
                    <Typography variant='body2' sx={{ m: 0, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                      <strong>Difference:</strong> {formatCurrency(tableState.difference.toString())}
                    </Typography>
                  </Alert>
                )}
              </Box>
            );
          })}

          <Box sx={{ mt: 1 }}>
            <Typography variant='caption' sx={{ fontStyle: 'italic', color: 'text.secondary', m: 0, lineHeight: 1.2, display: 'block' }}>
              * Not reviewed by CDCC
            </Typography>
            <Typography variant='caption' sx={{ fontStyle: 'italic', color: 'text.secondary', m: 0, lineHeight: 1.2, display: 'block' }}>
              Click Application # to indicate that a draw was unreviewed
            </Typography>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label='Note'
                value={comment}
                onChange={e => handleCommentChange(e.target.value)}
                variant='outlined'
                placeholder={autoComment.match ? autoComment.comment : 'Previous fundings note'}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                  },
                }}
              />
              <IconButton size='small' onClick={handleRefreshComment} sx={{ color: 'primary.main', mt: 1 }} title='Restore auto-comment'>
                <RefreshIcon />
              </IconButton>
              <BookmarkToggle
                addToActionItems={noteBookmarked}
                onToggle={handleToggleNoteBookmark}
                sx={{ mt: 1.5 }}
                ariaLabel='Add note to action items'
              />
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <CommentsSection
              commentsField={previousFundingsSectionCommentsField}
              onChange={handlePreviousFundingsSectionCommentsChange}
              title='Comments'
              onBook={onBook}
            />
          </Box>
        </Box>
        <ActionItemsReorderModal open={reorderModalOpen} onClose={() => setReorderModalOpen(false)} />
      </AccordionDetails>
    </Accordion>
  );
};

export default PreviousFundingsSection;
